import { useState, useMemo } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { MagnifyingGlass, CalendarBlank, Plus } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { MaintenanceCalendar } from "../sections/MaintenanceCalendar";
import { OverdueMaintenanceList } from "../sections/OverdueMaintenanceList";
import { MaintenanceCreateDialog } from "components/assets/MaintenanceCreateDialog";
import {
  useAllMaintenanceSchedules,
  useMaintenanceEvents,
  useAssetCategories,
} from "hooks/useAssets";

const PlanningPage = () => {
  const { schedules, isLoading, error, refetch } =
    useAllMaintenanceSchedules();
  const { events, refetch: refetchEvents } = useMaintenanceEvents();
  const { categories } = useAssetCategories();

  const handleRefetch = async () => {
    await Promise.all([refetch(), refetchEvents()]);
  };
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedBuilding, setSelectedBuilding] = useState<string>("all");

  // Filter schedules based on filters
  const filteredSchedules = useMemo(() => {
    let result = schedules;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.asset?.name?.toLowerCase().includes(q) ||
          s.asset?.building?.id?.toLowerCase().includes(q)
      );
    }

    if (selectedCategory !== "all") {
      result = result.filter(
        (s) => s.asset?.assetCategory?.id?.toString() === selectedCategory
      );
    }

    if (selectedBuilding !== "all") {
      result = result.filter(
        (s) => s.asset?.building?.id === selectedBuilding
      );
    }

    return result;
  }, [schedules, searchQuery, selectedCategory, selectedBuilding]);

  // Filter events with same criteria
  const filteredEvents = useMemo(() => {
    let result = events;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.schedule?.name?.toLowerCase().includes(q) ||
          e.asset?.name?.toLowerCase().includes(q) ||
          e.asset?.building?.id?.toLowerCase().includes(q)
      );
    }

    if (selectedCategory !== "all") {
      result = result.filter(
        (e) => e.asset?.assetCategory?.id?.toString() === selectedCategory
      );
    }

    if (selectedBuilding !== "all") {
      result = result.filter(
        (e) => e.asset?.building?.id === selectedBuilding
      );
    }

    return result;
  }, [events, searchQuery, selectedCategory, selectedBuilding]);

  // Get unique buildings from schedules for the filter
  const buildingsInSchedules = useMemo(() => {
    const buildingIds = new Set<string>();
    for (const s of schedules) {
      if (s.asset?.building?.id) {
        buildingIds.add(s.asset.building.id);
      }
    }
    return Array.from(buildingIds).sort();
  }, [schedules]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Onderhoudsplanning | Onderhoud | Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalendarBlank size={24} className="text-primary" />
            <h1 className="text-2xl font-bold">Onderhoudsplanning</h1>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus size={16} className="mr-1" />
            Onderhoudsschema toevoegen
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap gap-3">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlass
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  placeholder="Zoeken op naam of gebouw..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Alle categorieën" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle categorieën</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedBuilding}
                onValueChange={setSelectedBuilding}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Alle gebouwen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle gebouwen</SelectItem>
                  {buildingsInSchedules.map((buildingId) => (
                    <SelectItem key={buildingId} value={buildingId}>
                      {buildingId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-sm text-muted-foreground">
                  Onderhoudsplanning laden...
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-sm text-red-600">
                  Fout bij laden: {error}
                </div>
              </div>
            ) : (
              <MaintenanceCalendar
                schedules={filteredSchedules}
                events={filteredEvents}
                onRefetch={handleRefetch}
              />
            )}
          </CardContent>
        </Card>

        {/* Overdue list */}
        {!isLoading && !error && (
          <OverdueMaintenanceList
            schedules={filteredSchedules}
            onRefetch={handleRefetch}
          />
        )}
      </div>

      <MaintenanceCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleRefetch}
      />
    </>
  );
};

export default PlanningPage;
